let memoryAccounts = [];
let memoryActiveAccountId = null;

// Create-profile drafts (see createProfileDraftStorage.js) survive a browser-storage wipe so an
// expired session doesn't silently throw away unfinished profiles.
const PRESERVED_LOCAL_STORAGE_KEY_PREFIX = "jk:create-profile-draft";

function normalizeStoredAccount(account) {
  if (!account?.user) return null;

  const user = account.user;
  const id = String(user?.id || account.id || "");
  if (!id) return null;
  const email = String(user?.email || "").trim().toLowerCase();

  return {
    id,
    user,
    email,
    session_expires_at: account.session_expires_at || account.access_token_expires_at || null,
  };
}

function canUseLocalStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function canUseSessionStorage() {
  return typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
}

function clearLegacyAuthStorageKeys() {
  const keys = [
    "jk_accounts",
    "jk_active_account_id",
    "jk_last_active_account_id",
    "token",
    "user",
  ];

  try {
    if (canUseLocalStorage()) {
      keys.forEach((key) => window.localStorage.removeItem(key));
    }
  } catch (error) {
    console.warn("Failed to clear legacy auth localStorage:", error);
  }

  try {
    if (canUseSessionStorage()) {
      keys.forEach((key) => window.sessionStorage.removeItem(key));
    }
  } catch (error) {
    console.warn("Failed to clear legacy auth sessionStorage:", error);
  }
}

export function getStoredAccounts() {
  clearLegacyAuthStorageKeys();
  return memoryAccounts;
}

export function getStoredActiveAccountId() {
  clearLegacyAuthStorageKeys();
  return memoryActiveAccountId;
}

export function getActiveStoredAccount(accounts = getStoredAccounts(), activeAccountId = getStoredActiveAccountId()) {
  if (!accounts.length) return null;
  return accounts.find((account) => account.id === activeAccountId) || accounts[0];
}

export function persistStoredAccounts(accounts, activeAccountId = null) {
  const dedupedMap = new Map();
  accounts
    .map(normalizeStoredAccount)
    .filter(Boolean)
    .forEach((account) => {
      dedupedMap.set(account.id, account);
    });

  memoryAccounts = Array.from(dedupedMap.values());
  const nextActiveAccount =
    memoryAccounts.find((account) => account.id === activeAccountId) || memoryAccounts[0] || null;
  memoryActiveAccountId = nextActiveAccount?.id || null;
  clearLegacyAuthStorageKeys();

  return {
    accounts: memoryAccounts,
    activeAccountId: memoryActiveAccountId,
    activeAccount: nextActiveAccount,
  };
}

export function upsertStoredAccount(sessionData) {
  const account = normalizeStoredAccount(sessionData);
  if (!account) {
    return persistStoredAccounts(memoryAccounts, memoryActiveAccountId);
  }

  return persistStoredAccounts([...memoryAccounts, account], account.id);
}

export function setStoredActiveAccount(accountId) {
  return persistStoredAccounts(memoryAccounts, accountId);
}

export function updateStoredAccountUser(accountId, nextUser) {
  const nextAccounts = memoryAccounts.map((account) =>
    account.id === String(accountId)
      ? normalizeStoredAccount({ ...account, user: nextUser })
      : account
  );

  return persistStoredAccounts(nextAccounts, accountId);
}

export function removeStoredAccount(accountId) {
  const nextAccounts = memoryAccounts.filter((account) => account.id !== String(accountId));
  const nextActiveId =
    memoryActiveAccountId === String(accountId) ? nextAccounts[0]?.id || null : memoryActiveAccountId;

  return persistStoredAccounts(nextAccounts, nextActiveId);
}

export function clearStoredAccounts() {
  return persistStoredAccounts([], null);
}

export async function clearBrowserStorageData() {
  const result = clearStoredAccounts();

  try {
    if (canUseSessionStorage()) window.sessionStorage.clear();
  } catch (error) {
    console.warn("Failed to clear sessionStorage:", error);
  }

  // Unfinished create-profile drafts are deliberately kept: this runs on session expiry too, and
  // wiping them there is what made drafts look like they only survived ~20 minutes. An explicit
  // logout still clears them first (see clearAllCreateProfileDraftsForUser in App.js).
  try {
    if (canUseLocalStorage()) {
      const preserved = [];
      for (let index = 0; index < window.localStorage.length; index += 1) {
        const key = window.localStorage.key(index);
        if (key && key.startsWith(PRESERVED_LOCAL_STORAGE_KEY_PREFIX)) {
          preserved.push([key, window.localStorage.getItem(key)]);
        }
      }

      window.localStorage.clear();
      preserved.forEach(([key, value]) => {
        if (value !== null) window.localStorage.setItem(key, value);
      });
    }
  } catch (error) {
    console.warn("Failed to clear localStorage:", error);
  }

  try {
    if (typeof window !== "undefined" && "caches" in window) {
      const cacheNames = await window.caches.keys();
      await Promise.all(cacheNames.map((cacheName) => window.caches.delete(cacheName)));
    }
  } catch (error) {
    console.warn("Failed to clear Cache Storage:", error);
  }

  try {
    if (
      typeof window !== "undefined" &&
      window.indexedDB &&
      typeof window.indexedDB.databases === "function"
    ) {
      const databases = await window.indexedDB.databases();
      await Promise.all(
        databases
          .map((database) => database?.name)
          .filter(Boolean)
          .map(
            (databaseName) =>
              new Promise((resolve) => {
                const request = window.indexedDB.deleteDatabase(databaseName);
                request.onsuccess = resolve;
                request.onerror = resolve;
                request.onblocked = resolve;
              })
          )
      );
    }
  } catch (error) {
    console.warn("Failed to clear IndexedDB:", error);
  }

  return result;
}
